import { PatientAccessCallAnswers } from '@bariendo/lib/patient-access-note';
import { PAC_PREFILL_FIELD_IDS } from '@bariendo/lib/patient-access-script';
import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useEffect } from 'react';
import { readEligibilityPrefill } from './eligibilityPrefill';

/**
 * Pre-fills procedure, goal and prior procedure from the patient's eligibility
 * form, without clobbering anything the rep has already entered.
 *
 * Shared by the intro-call and PN consultation cards, which record those three
 * under the same ids. `updateAnswers` must be the draft hook's — its no-op
 * short-circuit is what keeps a re-run on every live-timeline poll from
 * queueing a redundant write.
 *
 * `fields` names the answers this card takes; the default is the set every card
 * shares, so the intro-call cards never receive the PN card's conditions, which
 * they have no row for. `enabled: false` neither reads nor writes: the update
 * autosaves, so a card showing a note already saved to the chart must pass it.
 */
export function useEligibilityPrefill<T extends PatientAccessCallAnswers>(
  patient: Patient | undefined,
  updateAnswers: (mutate: (current: T) => T) => void,
  { fields = PAC_PREFILL_FIELD_IDS, enabled = true }: { fields?: readonly string[]; enabled?: boolean } = {}
): void {
  const medplum = useMedplum();
  // A key rather than the array, so a caller passing a fresh array each render
  // does not re-read the form on every render.
  const fieldKey = fields.join(',');

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const accepted = fieldKey.split(',');

    readEligibilityPrefill(medplum, patient)
      .then((prefill) => {
        const entries = Object.entries(prefill).filter(
          ([id, value]) => accepted.includes(id) && (Array.isArray(value) ? value.length > 0 : Boolean(value))
        );
        if (cancelled || entries.length === 0) return;

        updateAnswers((current) => {
          const values = { ...current.values };
          for (const [id, value] of entries) {
            // An empty list is truthy, so chips the navigator cleared stay cleared.
            if (!values[id]) values[id] = value;
          }
          return { ...current, values };
        });
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [medplum, patient, updateAnswers, fieldKey, enabled]);
}
